import detectIncognito from 'detectincognitojs'
import { omitBy } from 'lodash'
import mixpanel from 'mixpanel-browser'
import { GetStaticProps, NextPage } from 'next'
import dynamic from 'next/dynamic'
// import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { Dispatch, Fragment, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AnnouncementNotification } from 'src/components/AnnouncementNotification/AnnouncementNotification'
import { CompatibleLink } from 'src/components/CompatibleLink/CompatibleLink'
import { getFingerprint } from 'src/components/CustomHooks/useFingerprint'
// import { EnhancedRequestContainer } from 'src/components/EditListing/EnhancedRequestContainer'
import { Footer } from 'src/components/Footer/Footer'
import { Breadcrumb } from 'src/components/global/Breadcrumb'
import MainContainer from 'src/components/global/MainContainer/MainContainer'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { IltaHeader } from 'src/components/IltaHeader/IltaHeader'
import FreeSearchPopup from 'src/components/Popup/FreeSearchPopup'
import SkeletonLoading from 'src/components/SkeletonLoading/SkeletonLoading'
import { SolutionBreadcrumb } from 'src/components/SolutionBreadcrumb/SolutionBreadcrumb'
// import { SolutionEnhancedHeader } from 'src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader'
// import { SolutionHeader } from 'src/components/SolutionsPage/SolutionHeader/SolutionHeader'
import { Testimonials } from 'src/components/Testimonials/Testimonials'
// import { VendorDetailContainer } from 'src/components/VendorDetailContainer/VendorDetailContainer'
// import { VendorEnhancedDetailContainer } from 'src/components/VendorEnhancedDetailContainer/VendorEnhancedDetailContainer'
import {
  AMPLIFY_USER_POOL_APP_CLIENT_ID,
  MIXPANEL_ENABLED,
  SHOW_FREE_SEARCH,
  SHOW_RELATED_CONTENT,
  SHOW_RELATED_CONTENT_TO_ALL,
  THEME,
} from 'src/config/config'
import { apolloSdk } from 'src/graphql/apolloSdk'
import {
  IProductSolutionDataFragment,
  ISimilarSolutionItemFragment,
  ITestimonialItemFragment,
  useCheckFreeSearchLazyQuery,
  useRelatedContentBySolutionLazyQuery,
  useShowRelatedContentFeatureLazyQuery,
  useSimilarSolutionsLazyQuery,
} from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'
import { Meta } from 'src/layout/Meta'
import { showToast } from 'src/store/actions/toastAction'
import { Main } from 'src/templates/Main'
import { REVIEWS_TAB } from 'src/types'
import { withRetries } from 'src/utils/retryHelper'

import { AppState } from '../../store/types'

type Props = {
  solution: IProductSolutionDataFragment
  testimonials: ITestimonialItemFragment[]
  showVendorAnalysis: boolean
  analysisTotal: number
  productBriefingTotal: number
  showProductBriefing: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subTopic: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  topic: any
  vendorUsers: string[]
  _testimonials: ITestimonialItemFragment[]
}

const EnhancedRequestContainer = dynamic(
  () => import('src/components/EditListing/EnhancedRequestContainer').then((mod) => mod.EnhancedRequestContainer),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const SolutionEnhancedHeader = dynamic(
  () =>
    import('src/components/SolutionsPage/SolutionEnhancedHeader/SolutionEnhancedHeader').then(
      (mod) => mod.SolutionEnhancedHeader,
    ),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const SolutionHeader = dynamic(
  () => import('src/components/SolutionsPage/SolutionHeader/SolutionHeader').then((mod) => mod.SolutionHeader),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const VendorDetailContainer = dynamic(
  () => import('src/components/VendorDetailContainer/VendorDetailContainer').then((mod) => mod.VendorDetailContainer),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)
const VendorEnhancedDetailContainer = dynamic(
  () =>
    import('src/components/VendorEnhancedDetailContainer/VendorEnhancedDetailContainer').then(
      (mod) => mod.VendorEnhancedDetailContainer,
    ),
  {
    loading: () => <SkeletonLoading className="bg-navy" />,
  },
)

const VendorPage: NextPage<Props> = ({
  solution,
  testimonials,
  showVendorAnalysis,
  analysisTotal,
  productBriefingTotal,
  showProductBriefing,
  subTopic,
  topic,
  vendorUsers,
  _testimonials,
}: Props) => {
  const originInfo = useSelector((state: AppState) => state.originInfo)
  const prevPath = originInfo.lastOrigin || ''
  const [isFreeSearch, setIsFreeSearch] = useState<boolean | null>(null)
  const [checkFreeSearch, { data: checkFreeSearchData }] = useCheckFreeSearchLazyQuery()
  const [isNonLogin, setIsNonLogin] = useState(false)

  const [tabIdx, setTabIdx] = useState(0)
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()

  const onSelectTab = (index: number) => {
    setTabIdx(index)
    // Reset sort, search keyword, filters, and pagination to default when switching tabs
    // This ensures each tab starts fresh without filters/search from other tabs
    updateQueryParams({
      sort: undefined,
      keyword: undefined,
      resourceType: undefined,
      page: undefined,
    })
  }

  const displayName = solution?.displayName || ''
  const description = solution?.shortDescription || ''
  const slug = solution?.slug || ''
  const subTopics = solution?.subTopics || []
  const topics = solution?.topics || []
  const imageUrl = useDefaultLogo(solution?.logo?.url)
  const isEnhanced = solution?.isEnhanced
  const isPremium = solution?.isPremium
  // const { announcement, optionalLink, publishedAt } = solution?.attributes?.announcement?.data?.attributes ?? {}

  const emptyAnnouncement = {
    announcement: '',
    optionalLink: '',
    publishedAt: '',
    isArchived: false,
  }
  const activeAnnouncements = solution?.announcement?.filter((item) => item != null && !item.isArchived) ?? []
  const announcementData = activeAnnouncements[0] ?? emptyAnnouncement
  const { announcement = '', optionalLink = '', publishedAt = '' } = announcementData

  const { session } = useAuthenticated()

  const [loadSimilarSolutions, { data: similarSolutionsData }] = useSimilarSolutionsLazyQuery()
  const [relatedContent, { data: relatedContentData }] = useRelatedContentBySolutionLazyQuery()

  // const similarSolutionsData = [{ similarSolutions: { solutions: [] } }]
  // const relatedContentData = [{ relatedContentBySolution: { data: { contents: [], total: 0 } } }]

  const [solutionRelatedContent, setSolutionRelatedContent] = useState<any[]>()
  const [totalRelatedContent, setTotalRelatedContent] = useState(0)
  const dispatch: Dispatch<any> = useDispatch()
  const randomNum = solution.id ?? solution?.slug ?? Math.floor(Math.random() * 1000) // Generates a number between 0 and 999
  const [loadShowRelatedContentFeature, { data: permissionData }] = useShowRelatedContentFeatureLazyQuery()
  // const permissionData = [{ showRelatedContentFeature: false }]

  const [checkFeaturePermission, setCheckFeaturePermission] = useState(false)

  // const similars = useMemo<ISimilarSolutionItemFragment[]>(() => {
  //   return (
  //     similarSolutionsData?.similarSolutions?.solutions
  //       ?.filter((item): item is ISimilarSolutionItemFragment => !!item) ?? []
  //   )
  // }, [similarSolutionsData])

  const similars = useMemo<ISimilarSolutionItemFragment[]>(() => {
    return (
      similarSolutionsData?.similarSolutions?.solutions.filter(
        (item): item is ISimilarSolutionItemFragment => !!item,
      ) || []
    )
  }, [similarSolutionsData])

  useEffect(() => {
    const fetchData = async () => {
      if (typeof window !== 'undefined') {
        const lastAuthUser =
          localStorage.getItem(`CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`) ||
          localStorage.getItem(`sso`)
        setIsNonLogin(!lastAuthUser)

        try {
          const browser = await detectIncognito()
          const fingerprintID = await getFingerprint()
          const customAccessQuery = solution?.documentId

          if (SHOW_FREE_SEARCH && fingerprintID && customAccessQuery) {
            const query = {
              accessFreeSearch: true,
              uniqueAddress: !browser?.isPrivate && browser?.browserName !== '' ? fingerprintID : '',
              customAccessQuery: customAccessQuery,
            }

            await checkFreeSearch({ variables: query, fetchPolicy: 'no-cache' })
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Error in checking free search is available:')
        }
      }
    }
    if (slug) {
      fetchData()
    }
  }, [slug])

  useEffect(() => {
    if (checkFreeSearchData) {
      // console.log(checkFreeSearchData)

      const isFreeSearch = checkFreeSearchData?.checkFreeSearch?.isFreeSearch
      if (isFreeSearch != null) {
        setIsFreeSearch(isFreeSearch)
      }

      if (
        isNonLogin &&
        checkFreeSearchData &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearch == true &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearchLeft == 1
      ) {
        dispatch(showToast('This is your last free search for the week. ', 'non_r_last_free_search'))
      }
      if (
        !isNonLogin &&
        checkFreeSearchData &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearch == true &&
        checkFreeSearchData?.checkFreeSearch?.isFreeSearchLeft == 1
      ) {
        dispatch(showToast('This is your last free search for the week. ', 'r_last_free_search'))
      }
    }
  }, [checkFreeSearchData])

  useEffect(() => {
    const fetchData = async () => {
      try {
        await loadShowRelatedContentFeature()
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in checking that user have Show Related Content Permission')
      }
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (permissionData?.showRelatedContentFeature || SHOW_RELATED_CONTENT_TO_ALL) {
      setCheckFeaturePermission(permissionData?.showRelatedContentFeature || SHOW_RELATED_CONTENT_TO_ALL)
    }
  }, [permissionData])

  const fetchData = () => {
    if (session?.user?.idToken && session?.user?.idToken !== '') {
      loadSimilarSolutions({
        variables: {
          id: solution?.documentId || '',
        },
        context: {
          headers: {
            Authorization: `Bearer ${session?.user?.idToken}`,
          },
        },
      })
    } else {
      loadSimilarSolutions({
        variables: {
          id: solution?.documentId || '',
        },
      })
    }

    const query = {
      offset: 0,
      limit: 10,
      sort: 'Category_Chronologically',
      slug: slug,
      relatedContent: 1,
    }
    if (SHOW_RELATED_CONTENT) {
      relatedContent({ variables: { query: omitBy(query, (value) => !value) } })
    }
  }

  useEffect(() => {
    if (relatedContentData?.relatedContentBySolution?.data?.contents) {
      setSolutionRelatedContent(relatedContentData?.relatedContentBySolution?.data?.contents || [])
      setTotalRelatedContent(relatedContentData?.relatedContentBySolution?.data?.total || 0)
    }
  }, [relatedContentData])

  useEffect(() => {
    fetchData()
    if (router.query['from'] === 'submitReview') onSelectTab(REVIEWS_TAB)
  }, [router])

  useEffect(() => {
    if (MIXPANEL_ENABLED) {
      mixpanel.track('visit_product', {
        model: 'Vendor',
        id: solution.documentId,
        displayName,
        subtopic: subTopics?.map((item) => item?.name),
        isEnhanced,
        isPremium,
      })
    }
  }, [solution, displayName, isEnhanced, isPremium])

  const meta = (
    <Meta
      title={displayName}
      description={description}
      url={`/vendors/${slug}`}
      image={imageUrl}
      keywords={(topics || [])
        .map((topic) => topic?.name || '')
        .concat((subTopics || []).map((subTopic) => subTopic?.name || ''))}
    />
  )
  const freeSearchWithAllCTA = (
    <FreeSearchPopup
      heading={`You have reached your final search for the week.`}
      title={<>Register a free account on LTH to continue searching.</>}
      createAccountButton={true}
      premiumSignupButton={false}
      foundSolution={false}
    />
  )

  const freeSearchWithPremiumButton = (
    <FreeSearchPopup
      heading={`You have reached your final free search for the week.`}
      title={
        <>
          Sign up to LTH <span className="text-[#3329EF]">Premium</span> for unlimited searches.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
      foundSolution={false}
    />
  )

  // console.log('####VendorPage::isEnhanced--------------------->', isEnhanced)
  return (
    <Main meta={meta}>
      <MainContainer id="mainContainer">
        <ThemeOnly theme="ilta">
          <IltaHeader />
        </ThemeOnly>
        <div className="flex flex-col bg-clouds">
          <div className="flex flex-col w-full items-center">
            {router.query['viewType'] === 'manage' && (
              <Breadcrumb
                key={`${randomNum}-Breadcrumb`}
                subject={'MY ACCOUNT'}
                subjectRoute={'/my-account'}
                subtopic={'MY SOLUTIONS'}
                subtopicRoute={'/my-solutions'}
                subtitle={displayName}
              />
            )}
            {router.query['viewType'] !== 'manage' && (
              <Fragment>
                {prevPath ? (
                  <SolutionBreadcrumb key={`${randomNum}-SolutionBreadcrumb`} />
                ) : (
                  <div className="!max-w-[1100px] flex flex-row w-full mt-[20px] mb-[10px] pl-[1.25rem]">
                    {topic && (
                      <CompatibleLink key={`${randomNum}-topic-CompatibleLink`} href={`/topics/${topic?.slug}/`}>
                        {topic?.name}
                      </CompatibleLink>
                    )}
                    {topic && subTopic && (
                      <Fragment>
                        <span className="font-primary font-bold text-[15px] text-textdark mx-[10px]">/</span>
                        <CompatibleLink
                          key={`${randomNum}-subTopic-CompatibleLink`}
                          href={`/topics/${topic?.slug}/${subTopic?.slug}`}
                        >
                          {subTopic?.name}
                        </CompatibleLink>
                      </Fragment>
                    )}
                    {!topic && !subTopic ? (
                      <span className="font-primary font-bold text-[15px] text-textdark mx-[10px]">{displayName}</span>
                    ) : null}
                  </div>
                )}
              </Fragment>
            )}
          </div>

          {(isEnhanced || isPremium) && !!solution?.productData ? (
            <SolutionEnhancedHeader
              key={`${randomNum}-SolutionEnhancedHeader`}
              solution={solution}
              onSelectTab={onSelectTab}
              tabIdx={tabIdx}
              showVendorAnalysis={showVendorAnalysis}
              showProductBriefing={showProductBriefing}
              vendorUsers={vendorUsers}
            />
          ) : (
            <SolutionHeader
              key={`${randomNum}-SolutionHeader`}
              solution={solution}
              onSelectTab={onSelectTab}
              tabIdx={tabIdx}
              showVendorAnalysis={showVendorAnalysis}
              showProductBriefing={showProductBriefing}
              vendorUsers={vendorUsers}
            />
          )}
        </div>
        <hr />
        {announcement && publishedAt && (
          <div className="flex justify-center">
            <div className="flex flex-col pl-[1.25rem] pr-[1.25rem] !max-w-[1100px] w-full">
              {optionalLink ? (
                <AnnouncementNotification
                  key={`${randomNum}-AnnouncementNotification-link`}
                  announcement={announcement}
                  publishedAt={publishedAt}
                  optionalLink={optionalLink}
                />
              ) : (
                <AnnouncementNotification
                  key={`${randomNum}-AnnouncementNotification-nolink`}
                  announcement={announcement}
                  publishedAt={publishedAt}
                />
              )}
            </div>
          </div>
        )}

        {!isEnhanced && router.query['viewType'] === 'manage' && vendorUsers?.includes(session?.user.id || '') && (
          <EnhancedRequestContainer
            key={`${randomNum}-EnhancedRequestContainer`}
            solution_id={solution.documentId ?? ''}
          />
        )}

        <div className="flex flex-row justify-center w-full">
          <div
            className={`flex flex-row justify-between w-full mb-32 ${!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch)) ? '!max-w-[1100px]' : 'show-popup-search-rectangle'}`}
          >
            {isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithAllCTA}
            {!isNonLogin && !isFreeSearch && isFreeSearch != null && freeSearchWithPremiumButton}
            {(!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
              ((isEnhanced || isPremium) && !!solution?.productData ? (
                <VendorEnhancedDetailContainer
                  key={`${randomNum}-VendorEnhancedDetailContainer`}
                  vendor={solution}
                  tabIdx={tabIdx}
                  similars={similars}
                  analysisTotal={analysisTotal}
                  productBriefingTotal={productBriefingTotal}
                  testimonials={testimonials}
                  relatedContent={checkFeaturePermission ? solutionRelatedContent : []}
                  totalRelatedContent={checkFeaturePermission ? totalRelatedContent : 0}
                />
              ) : (
                <VendorDetailContainer
                  key={`${randomNum}-VendorEnhancedDetailContainer`}
                  solution={solution}
                  tabIdx={tabIdx}
                  similars={similars}
                  analysisTotal={analysisTotal}
                  productBriefingTotal={productBriefingTotal}
                  slug={slug}
                  relatedContent={checkFeaturePermission ? solutionRelatedContent : []}
                  totalRelatedContent={checkFeaturePermission ? totalRelatedContent : 0}
                />
              ))}
          </div>
        </div>
        {(!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
          (isEnhanced || isPremium) &&
          testimonials?.length > 0 && (
            <ThemeOnly theme="legaltech">
              <Testimonials key={`${randomNum}-Testimonials`} testimonials={testimonials || []} />
            </ThemeOnly>
          )}
        {(!SHOW_FREE_SEARCH || (SHOW_FREE_SEARCH && (isFreeSearch == null || isFreeSearch))) &&
          ((!isEnhanced && !isPremium) || (THEME === 'ilta' && <Testimonials testimonials={_testimonials} />))}
        <ThemeOnly theme="legaltech">
          <Footer />
        </ThemeOnly>
      </MainContainer>
    </Main>
  )
}

export default VendorPage

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  try {
    if (!params?.slug) return { revalidate: 60, notFound: true }

    const slug = params.slug as string

    const [{ solutions }, { testimonialsPlural }] = await Promise.all([
      withRetries(() => apolloSdk.productBySlug({ slug, includeIlta: THEME === 'ilta' })),
      withRetries(() => apolloSdk.globalTestimonials()),
    ])

    const solution = solutions && solutions.length > 0 ? solutions[0] : null
    if (!solution) return { revalidate: 60, notFound: true }

    const subTopics = solution?.subTopics || []
    let topics = solution?.topics || []
    if (topics.length === 0 && subTopics.length > 0) {
      const queryVar = { slug: subTopics[0]?.slug || '' }
      const { subTopics: sub } = await withRetries(() => apolloSdk.getSubTopicBySlug(queryVar))
      topics = (sub || [])[0]?.topics || []
    }

    const { contentResults } = await withRetries(() =>
      apolloSdk.searchContentsTotal({
        query: {
          contentType: 'Vendor_Analysis',
          resourceSolutionIds: solution.documentId || '',
        },
      }),
    )
    const { contentResults: productBriefingContentResults } = await withRetries(() =>
      apolloSdk.searchContents({
        query: { documentType: 'Product Briefing', resourceSolutionIds: solution.documentId || '' },
      }),
    )

    const vendorUsers =
      (solution?.vendor?.users.map((user) => user?.external_id || null).filter((item) => item !== null) as string[]) ||
      []

    // console.log('contentResults', contentResults)
    // console.log('solution?.vendor?.users', solution?.vendor?.users)

    // console.log('solution-object.enhancedListingData', solution?.productData?.enhancedListingData)
    // const testimonials = testimonialsPlural?.data || []
    const testimonials: any[] = (solution?.productData?.enhancedListingData?.testimonials || []).map((testimonial) => {
      return {
        ...testimonial,
        description: testimonial?.description || '',
        author: {
          name: testimonial?.author?.name || '',
          role: testimonial?.author?.role || '',
          company: testimonial?.author?.company || '',
          avatar: {
            url: testimonial?.author?.avatar?.url || '',
          },
        },
      }
    })

    return {
      props: {
        solution,
        testimonials,
        showVendorAnalysis: contentResults?.data?.total !== 0,
        analysisTotal: contentResults?.data?.total || 0,
        productBriefingTotal: productBriefingContentResults?.data?.total || 0,
        showProductBriefing: productBriefingContentResults?.data?.total !== 0,
        subTopic: subTopics[0] || null,
        topic: topics[0] || null,
        vendorUsers,
        _testimonials: (testimonialsPlural || []).filter((item): item is ITestimonialItemFragment => item !== null),
      },
      revalidate: 60, // Only cache successful pages
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching vendor:', error)
    return {
      notFound: true,
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  const { solutions } = await apolloSdk.allSolutionSlugs({ start: 0, type: 'Product' })
  const result = solutions || []
  const paths = result.filter((item) => !!item).map((item) => ({ params: { slug: item?.slug } }))

  return {
    paths,
    fallback: 'blocking',
  }
}
